import { useState } from "react";
import { monthNames, getDaysInMonth, getFirstDayOfMonth, isSameDay, formatDate, styles } from "./base/BaseCalendar";
import { theme } from "../theme.js";

export default function DateRangePicker({ onDone, startDate, endDate, onClose}) {
  const [currentMonth1, setCurrentMonth1] = useState(new Date());
  const [currentMonth2, setCurrentMonth2] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1));
  const [localStartDate, setLocalStartDate] = useState(startDate ? (typeof startDate === 'string' ? new Date(startDate) : startDate) : null);
  const [localEndDate, setLocalEndDate] = useState(endDate ? (typeof endDate === 'string' ? new Date(endDate) : endDate) : null);

  const handleDateClick = (day, monthDate) => {
    const selectedDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);

    if (localStartDate && localEndDate) {
      setLocalStartDate(selectedDate);
      setLocalEndDate(null);
    } else if (localStartDate && !localEndDate) {
      if (selectedDate < localStartDate) {
        setLocalStartDate(selectedDate);
      } else {
        setLocalEndDate(selectedDate);
      }
    } else {
      setLocalStartDate(selectedDate);
    }
  };

  const handlePrevMonth1 = () => {
    setCurrentMonth1(new Date(currentMonth1.getFullYear(), currentMonth1.getMonth() - 1));
    setCurrentMonth2(new Date(currentMonth2.getFullYear(), currentMonth2.getMonth() - 1));
  };

  const handleNextMonth1 = () => {
    setCurrentMonth1(new Date(currentMonth1.getFullYear(), currentMonth1.getMonth() + 1));
    setCurrentMonth2(new Date(currentMonth2.getFullYear(), currentMonth2.getMonth() + 1));
  };

  const handlePrevMonth2 = () => {
    setCurrentMonth2(new Date(currentMonth2.getFullYear(), currentMonth2.getMonth() - 1));
    setCurrentMonth1(new Date(currentMonth1.getFullYear(), currentMonth1.getMonth() - 1));
  };

  const handleNextMonth2 = () => {
    setCurrentMonth2(new Date(currentMonth2.getFullYear(), currentMonth2.getMonth() + 1));
    setCurrentMonth1(new Date(currentMonth1.getFullYear(), currentMonth1.getMonth() + 1));
  };

  const handleDone = () => {
    if (localStartDate && localEndDate && onDone) {
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      onDone(
        formatDate(localStartDate), 
        formatDate(localEndDate)
      );
    }
  };

  const handleReset = () => {
    setLocalStartDate(null);
    setLocalEndDate(null);
    setCurrentMonth1(new Date());
    setCurrentMonth2(new Date(new Date().getFullYear(), new Date().getMonth() + 1));
  };

  const renderCalendar = (monthDate) => {
    const daysInMonth = getDaysInMonth(monthDate);
    const firstDay = getFirstDayOfMonth(monthDate);
    const days = [];
    const year = monthDate.getFullYear();

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} style={styles.emptyCell} />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, monthDate.getMonth(), day);
      const isStart = localStartDate && isSameDay(date, localStartDate);
      const isEnd = localEndDate && isSameDay(date, localEndDate);
      const isInRange = localStartDate && localEndDate && date > localStartDate && date < localEndDate;

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDateClick(day, monthDate)}
          style={{
            ...styles.day,
            ...(isStart || isEnd ? styles.selectedDay : {}),
            ...(isInRange ? styles.inRangeDay : {}),
          }}
        >
          {day}
        </button>
      );
    }

    return (
      <div style={styles.calendar}>
        <h3 style={styles.monthTitle}>{monthNames[monthDate.getMonth()]} {year}</h3>
        <div style={styles.weekDays}>
          {["L", "M", "M", "J", "V", "S", "D"].map((day, idx) => (
            <div key={`${day}-${idx}`} style={styles.weekDay}>
              {day}
            </div>
          ))}
        </div>
        <div style={styles.daysGrid}>{days}</div>
      </div>
    );
  };

  return (
    <div style={{ ...styles.container, maxWidth: "750px" }}>
      <div style={styles.header}>
        <div style={styles.dateDisplay}>
          <div>
            <span style={styles.label}>Start</span>
            <span style={styles.dateValue}>{formatDate(localStartDate)}</span>
          </div>
          <span style={styles.separator}>→</span>
          <div>
            <span style={styles.label}>End</span>
            <span style={styles.dateValue}>{formatDate(localEndDate)}</span>
          </div>
        </div>
        <button type="button" onClick={onClose} style={styles.resetBtn}>
          ✕
        </button>
      </div>

      <div style={styles.calendarsContainer}>
        <div style={styles.calendarWrapper}>
          <button type="button" onClick={handlePrevMonth1} style={styles.navBtn}>
            ←
          </button>
          {renderCalendar(currentMonth1)}
          <button type="button" onClick={handleNextMonth1} style={styles.navBtn}>
            →
          </button>
        </div>

        <div style={styles.calendarWrapper}>
          <button type="button" onClick={handlePrevMonth2} style={styles.navBtn}>
            ←
          </button>
          {renderCalendar(currentMonth2)}
          <button type="button" onClick={handleNextMonth2} style={styles.navBtn}>
            →
          </button>
        </div>
      </div>

      <div style={styles.footer}>
        <button type="button" onClick={handleReset} style={styles.backBtn}>
          Reset
        </button>
        <button type="button" onClick={handleDone} style={styles.doneBtn} disabled={!localStartDate || !localEndDate}>
          Done
        </button>
      </div>
    </div>
  );
}